import moment from "moment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logoImg from "../../../../assets/images/logo-img.png";

// Generic "Download Chart" PDF export, shared by every report. Unlike the old
// per-report google-charts version (which had to redraw a hidden, high-res
// copy of the chart before it could be rasterized), Chart.js already renders
// a real <canvas> on screen, so we can html2canvas-capture the live
// #pdf-content node directly.
export const exportChartPdf = async ({
  elementId,
  title,
  dealerCompany,
  dealerId,
  dataRangeFrom,
  dataRangeTo,
  selectedGemstoneValue,
  totalClicks,
  fileName,
  infoLines,
}) => {
  const node = document.getElementById(elementId);
  if (!node) return;

  // An empty report (no chart rendered) gives a zero-size node; html2canvas
  // then yields a 0x0 canvas that jsPDF rejects, so skip the capture and
  // still export the header/info/footer with a "no data" note instead.
  const hasContent = node.offsetWidth > 0 && node.offsetHeight > 0;
  const canvas = hasContent
    ? await html2canvas(node, {
        useCORS: true,
        scale: 2.5,
        backgroundColor: "#ffffff",
        logging: false,
      })
    : null;
  const imgData = canvas ? canvas.toDataURL("image/png") : null;

  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 5;
  const headerHeight = 14;
  const footerHeight = 10;
  const infoBlockHeight = 20;
  const availableHeight = pageHeight - headerHeight - footerHeight - infoBlockHeight - 12;

  pdf.setFillColor(31, 38, 62);
  pdf.rect(0, 0, pageWidth, headerHeight, "F");
  try {
    pdf.addImage(logoImg, "PNG", margin, 4, 40, 10);
  } catch {}

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.setTextColor(255, 255, 255);
  pdf.text(title.toUpperCase(), pageWidth - margin, 11, { align: "right" });

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);

  const preparedFor = dealerCompany ? `Prepared For: ${dealerCompany} (${dealerId})` : "Prepared For: -";
  const dateRange = `Date Range: ${moment(dataRangeFrom).format("MMM DD, YYYY")} - ${moment(dataRangeTo).format("MMM DD, YYYY")}`;

  // infoLines lets non-gemstone reports (e.g. jewelry) supply their own info
  // block instead of the gemstone-specific "Selected Gemstone" line below.
  const infoLinesRaw = infoLines
    ? [preparedFor, dateRange, ...infoLines].filter(Boolean)
    : [
        preparedFor,
        dateRange,
        `Selected Gemstone: ${selectedGemstoneValue === "All" ? "All Gemstones" : selectedGemstoneValue === "Mined" ? "Mined Gemstones" : "Lab Grown Gemstones"}`,
        `Total Clicks: ${(totalClicks || 0).toLocaleString?.() ?? totalClicks ?? 0}`,
      ].filter(Boolean);
  const maxWidth = pageWidth - margin * 2;
  const wrappedInfoLines = infoLinesRaw.flatMap((line) => pdf.splitTextToSize(line, maxWidth));
  const lineHeight = 4.8;
  let currentY = headerHeight + 5;
  wrappedInfoLines.forEach((line) => {
    pdf.text(line, margin, currentY);
    currentY += lineHeight;
  });
  currentY += 4;

  if (canvas && canvas.width > 0 && canvas.height > 0) {
    const imgRatio = canvas.width / canvas.height;
    let imgWidthMM = pageWidth - margin * 3;
    let imgHeightMM = imgWidthMM / imgRatio;
    if (imgHeightMM > availableHeight) {
      imgHeightMM = availableHeight;
      imgWidthMM = imgHeightMM * imgRatio;
    }
    pdf.addImage(imgData, "PNG", margin, currentY, imgWidthMM, imgHeightMM, undefined, "FAST");
  } else {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(102, 102, 102);
    pdf.text("No data available for the selected filters.", pageWidth / 2, currentY + availableHeight / 2, {
      align: "center",
    });
  }

  // Dark navy band, centered "© {year} JewelCloud Inc. | Generated on..." -
  // this IS the approved footer design (Task: copy this design to every
  // report, 2026-09-09) - the real backend PDF's own footer was changed to
  // match this instead of the other way around.
  pdf.setFillColor(31, 38, 62);
  pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8);
  const footerText = `© ${new Date().getFullYear()} JewelCloud Inc. | Generated on ${moment().format("MMM DD, YYYY h:mm A")}`;
  pdf.text(footerText, pageWidth / 2, pageHeight - footerHeight / 2 + 2, { align: "center" });

  const watermarkText = "JewelCloud";
  pdf.setFontSize(90);
  pdf.setFont(undefined, "bolditalic");
  pdf.setTextColor(102, 102, 102);
  pdf.saveGraphicsState();
  pdf.setGState(pdf.GState({ opacity: 0.1 }));
  const centerX = pageWidth / 2;
  const centerY = pageHeight / 2;
  const textWidth = pdf.getTextWidth(watermarkText);
  const textHeight = pdf.internal.getFontSize() / pdf.internal.scaleFactor;
  const angleRad = (45 * Math.PI) / 180;
  const rotatedWidth = textWidth * Math.cos(angleRad) + textHeight * Math.sin(angleRad);
  const rotatedHeight = textWidth * Math.sin(angleRad) + textHeight * Math.cos(angleRad);
  pdf.text(watermarkText, centerX - rotatedWidth / 2, centerY + rotatedHeight / 4 + 40, { angle: 45 });
  pdf.restoreGraphicsState();

  pdf.save(fileName);
};

// "Download PDF" export styled like the Diamond reports' backend-rendered PDF
// (JewelCloud2ApiReports ReportPdfRenderer.cs + Theme/Brand.cs): Letter
// landscape, navy hero band (JewelCloud 2.0 wordmark, title, "period |
// Prepared for X | type" subtitle, accent stripe), Executive Summary KPI
// card, navy footer with page number, rotated light watermark. Same inputs
// and same captured #pdf-content node as exportChartPdf - only the page
// design differs.
const BRAND = {
  navy: [16, 24, 47], // #10182f
  accent: [59, 91, 219], // #3b5bdb
  textPrimary: [28, 35, 51], // #1c2333
  textMuted: [107, 114, 128], // #6b7280
  borderGray: [226, 229, 235], // #e2e5eb
  bannerBg: [238, 241, 251], // #eef1fb
  subtitle: [224, 224, 224], // Colors.Grey.Lighten2
};

// Mirrors ExportActivityPdfModelBuilder.FormatPeriod.
const formatPeriod = (from, to) => {
  const f = moment(from);
  const t = moment(to);
  if (f.date() === 1 && t.isSame(f.clone().endOf("month"), "day")) return f.format("MMMM YYYY");
  return f.year() === t.year()
    ? `${f.format("MMM D")} - ${t.format("MMM D, YYYY")}`
    : `${f.format("MMM D, YYYY")} - ${t.format("MMM D, YYYY")}`;
};

// Mirrors FormatDiamondTypeFilter: segment omitted entirely for "All".
const formatGemstoneType = (value) => {
  if (!value || value === "All") return "";
  if (value === "Mined") return "Mined Gemstone";
  if (value === "Lab") return "Lab Grown Gemstone";
  return value;
};

export const exportReportPdf = async ({
  elementId,
  title,
  dealerCompany,
  dataRangeFrom,
  dataRangeTo,
  selectedGemstoneValue,
  totalClicks,
  fileName,
}) => {
  const node = document.getElementById(elementId);
  if (!node) return;

  const canvas = await html2canvas(node, {
    useCORS: true,
    scale: 2.5,
    backgroundColor: "#ffffff",
    logging: false,
  });
  const imgData = canvas.toDataURL("image/png");

  const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const padX = 28;
  const footerHeight = 25;

  // Hero band
  const heroHeight = 121;
  const stripeHeight = 4;
  pdf.setFillColor(...BRAND.navy);
  pdf.rect(0, 0, pageWidth, heroHeight - stripeHeight, "F");
  pdf.setFillColor(...BRAND.accent);
  pdf.rect(0, heroHeight - stripeHeight, pageWidth, stripeHeight, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(255, 255, 255);
  pdf.text("JewelCloud", padX, 31);
  const wordmarkWidth = pdf.getTextWidth("JewelCloud");
  pdf.setFontSize(9);
  pdf.setTextColor(...BRAND.accent);
  pdf.text("2.0", padX + wordmarkWidth + 2, 31);

  pdf.setFontSize(30);
  pdf.setTextColor(255, 255, 255);
  pdf.text(title, padX, 69);

  const subtitleParts = [
    formatPeriod(dataRangeFrom, dataRangeTo),
    `Prepared for ${dealerCompany || "-"}`,
    formatGemstoneType(selectedGemstoneValue),
  ].filter(Boolean);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(13);
  pdf.setTextColor(...BRAND.subtitle);
  pdf.text(subtitleParts.join("    |    "), padX, 95, { maxWidth: pageWidth - padX * 2 });

  // Executive Summary + KPI card
  let y = heroHeight + 16;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.setTextColor(...BRAND.textPrimary);
  pdf.text("Executive Summary", padX, y + 12);
  y += 18 + 6;

  const kpiHeight = 50;
  const kpiWidth = pageWidth - padX * 2;
  pdf.setFillColor(...BRAND.bannerBg);
  pdf.setDrawColor(...BRAND.borderGray);
  pdf.setLineWidth(1);
  pdf.rect(padX, y, kpiWidth, kpiHeight, "FD");
  const ruleX = padX + 12 + 6;
  pdf.setFillColor(...BRAND.accent);
  pdf.rect(ruleX, y + 10, 2, kpiHeight - 20, "F");
  pdf.setFontSize(7);
  pdf.setTextColor(...BRAND.textMuted);
  pdf.text("TOTAL CLICKS", ruleX + 8, y + 17);
  pdf.setFontSize(16);
  pdf.setTextColor(...BRAND.textPrimary);
  pdf.text(`${(totalClicks || 0).toLocaleString?.() ?? totalClicks ?? 0}`, ruleX + 8, y + 37);
  y += kpiHeight + 14;

  // Captured report content
  const maxImgWidth = pageWidth - padX * 2;
  const maxImgHeight = pageHeight - footerHeight - 14 - y;
  const imgRatio = canvas.width / canvas.height;
  let imgWidth = maxImgWidth;
  let imgHeight = imgWidth / imgRatio;
  if (imgHeight > maxImgHeight) {
    imgHeight = maxImgHeight;
    imgWidth = imgHeight * imgRatio;
  }
  pdf.addImage(imgData, "PNG", padX, y, imgWidth, imgHeight, undefined, "FAST");

  // Footer band
  pdf.setFillColor(...BRAND.navy);
  pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(7.5);
  const footerY = pageHeight - footerHeight / 2 + 2.6;
  pdf.text(
    `© ${new Date().getFullYear()} JewelCloud Inc. | Generated on ${moment().format("MMM DD, YYYY h:mm A")}`,
    pageWidth / 2,
    footerY,
    { align: "center" }
  );
  pdf.setFont("helvetica", "normal");
  pdf.text("Page 1", pageWidth - padX, footerY, { align: "right" });

  // Watermark: 90pt bold, ~#EEEEEE, tilted 28° and centered on the page.
  const watermarkText = "JewelCloud";
  pdf.saveGraphicsState();
  pdf.setGState(pdf.GState({ opacity: 0.18 }));
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(90);
  pdf.setTextColor(158, 158, 158);
  const angle = 28;
  const rad = (angle * Math.PI) / 180;
  const wmWidth = pdf.getTextWidth(watermarkText);
  const wmCapHeight = 90 * 0.7;
  const wmX = pageWidth / 2 - (Math.cos(rad) * wmWidth) / 2 + (Math.sin(rad) * wmCapHeight) / 2;
  const wmY = pageHeight / 2 + (Math.sin(rad) * wmWidth) / 2 + (Math.cos(rad) * wmCapHeight) / 2;
  pdf.text(watermarkText, wmX, wmY, { angle });
  pdf.restoreGraphicsState();

  pdf.save(fileName);
};
